import {
  Alert,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { describeRpcError, usePermission } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import { ErrorState, LoadingState } from "@how/ui";
import { PersonDrawer } from "../people/PersonDrawer";
import {
  type AddedFromSubmission,
  type SubmissionPreview,
  useAddPersonFromSubmission,
  useSubmissionPreview,
} from "./form-queries";

type Channel = SubmissionPreview["consent"][number];
type Match = SubmissionPreview["matches"][number];
type Added = AddedFromSubmission & { name: string; formPhone: string | null };

/** How a person would say each channel, as the timeline says it. */
const CHANNEL_WORDS: Record<Channel, string> = {
  email: "email",
  sms: "text messages",
  phone: "phone calls",
  mail: "postal mail",
};

const words = (channels: readonly Channel[]) => channels.map((c) => CHANNEL_WORDS[c]).join(", ");

export interface SubmissionPersonProps {
  readonly formId: string;
  readonly submissionId: string;
  /** The person this submission was already added as, if it was. */
  readonly personId: string | null;
  /**
   * How it was added: matched or created when it arrived (#338 part 2), otherwise by staff. While it waits for the
   * person to confirm by email (ADR 0063), "Add these details now" stands in for "Add this person".
   */
  readonly matchOutcome?:
    "matched" | "created" | "needs_decision" | "awaiting_confirmation" | null | undefined;
}

/**
 * "Add this person" on a form submission (#304): turns what someone sent into a person, or attaches it to someone the
 * church already knows, with the consent they gave. Offered to someone who manages forms, people and their consent;
 * the database checks the same three and refuses everything else in its own words.
 */
export function SubmissionPerson({
  formId,
  submissionId,
  personId,
  matchOutcome = null,
}: SubmissionPersonProps) {
  const forms = usePermission("forms.manage", { write: true });
  const people = usePermission("people.write", { write: true });
  const preferences = usePermission("preferences.manage", { write: true });
  const allowed = forms.data === true && people.data === true && preferences.data === true;
  const [adding, setAdding] = useState(false);
  const [record, setRecord] = useState<string | null>(null);

  return (
    <>
      {personId !== null ? (
        <AddedAs
          submissionId={submissionId}
          matchOutcome={matchOutcome}
          onOpen={() => {
            setRecord(personId);
          }}
        />
      ) : allowed && matchOutcome !== "awaiting_confirmation" ? (
        <Button
          onClick={() => {
            setAdding(true);
          }}
        >
          Add this person
        </Button>
      ) : null}
      {/* Kept mounted, so Mantine hands focus back to the button when it closes (UI standards, #261). */}
      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
        title="Add this person"
        size="lg"
      >
        {adding ? (
          <AddPerson
            formId={formId}
            submissionId={submissionId}
            onOpen={(id) => {
              setAdding(false);
              setRecord(id);
            }}
            onDone={() => {
              setAdding(false);
            }}
          />
        ) : null}
      </Modal>
      <PersonDrawer
        personId={record}
        onClose={() => {
          setRecord(null);
        }}
      />
    </>
  );
}

/** A submission already turned into someone: who, how, and their record. */
function AddedAs({
  submissionId,
  matchOutcome,
  onOpen,
}: {
  submissionId: string;
  matchOutcome: SubmissionPersonProps["matchOutcome"];
  onOpen: () => void;
}) {
  const preview = useSubmissionPreview(submissionId);
  const name = preview.data?.personName ?? null;
  if (name === null) return null;
  return (
    <Group justify="space-between" wrap="wrap" gap="xs">
      <Text size="sm">
        {matchOutcome === "matched"
          ? `Matched to ${name} when it arrived.`
          : matchOutcome === "created"
            ? `Added as a new person, ${name}, when it arrived.`
            : `Added to your people as ${name}.`}
      </Text>
      <Button variant="default" size="xs" onClick={onOpen}>
        Open {name}&apos;s record
      </Button>
    </Group>
  );
}

function AddPerson({
  formId,
  submissionId,
  onOpen,
  onDone,
}: {
  formId: string;
  submissionId: string;
  onOpen: (personId: string) => void;
  onDone: () => void;
}) {
  const preview = useSubmissionPreview(submissionId);
  if (preview.isPending) return <LoadingState label="Reading the form" />;
  if (preview.isError)
    return <ErrorState message="Could not read this message. Please try again." />;
  return (
    <AddPersonForm
      formId={formId}
      submissionId={submissionId}
      preview={preview.data}
      onOpen={onOpen}
      onDone={onDone}
    />
  );
}

const names = z.object({
  firstName: z.string().trim().min(1, "A first name is needed").max(100),
  lastName: z.string().trim().min(1, "A last name is needed").max(100),
});

function AddPersonForm({
  formId,
  submissionId,
  preview,
  onOpen,
  onDone,
}: {
  formId: string;
  submissionId: string;
  preview: SubmissionPreview;
  onOpen: (personId: string) => void;
  onDone: () => void;
}) {
  const add = useAddPersonFromSubmission();
  const [result, setResult] = useState<Added | null>(null);
  // Someone known whose texts go to another number than the form's: staff are asked before attaching (#304).
  const [asking, setAsking] = useState<Match | null>(null);
  // Back from the question hands focus to the Attach button that asked it (UI standards, #310 review).
  const attachButtons = useRef(new Map<string, HTMLButtonElement>());
  const [returnTo, setReturnTo] = useState<string | null>(null);
  useEffect(() => {
    if (asking === null && returnTo !== null) {
      attachButtons.current.get(returnTo)?.focus();
      setReturnTo(null);
    }
  }, [asking, returnTo]);
  const form = useForm({
    initialValues: { firstName: preview.firstName ?? "", lastName: preview.lastName ?? "" },
    validate: zod4Resolver(names),
  });

  const done = (name: string) => (r: AddedFromSubmission) => {
    setResult({ ...r, name, formPhone: preview.phone });
    notifications.show({
      color: semanticColor("success"),
      title: r.created ? "Added" : "Attached",
      message: r.created
        ? `${name} is added to your people.`
        : `The message is attached to ${name}.`,
    });
  };

  if (result !== null) return <Result result={result} onOpen={onOpen} onDone={onDone} />;

  const attach = (match: Match, makeFormPhonePrimary?: boolean) => {
    add.mutate(
      {
        formId,
        submissionId,
        personId: match.personId,
        ...(makeFormPhonePrimary === true ? { makeFormPhonePrimary } : {}),
      },
      { onSuccess: done(match.name) },
    );
  };

  if (asking !== null && asking.textsTo !== null && preview.phone !== null)
    return (
      <AskMainNumber
        name={asking.name}
        formPhone={preview.phone}
        textsTo={asking.textsTo}
        pending={add.isPending}
        error={add.isError ? describeRpcError(add.error).message : null}
        onYes={() => {
          attach(asking, true);
        }}
        onNo={() => {
          attach(asking);
        }}
        onBack={() => {
          setReturnTo(asking.personId);
          setAsking(null);
        }}
      />
    );

  const first = form.values.firstName.trim();
  const last = form.values.lastName.trim();
  return (
    <form
      onSubmit={form.onSubmit(() => {
        const name = `${first} ${last}`;
        add.mutate(
          { formId, submissionId, firstName: first, lastName: last },
          { onSuccess: done(name) },
        );
      })}
    >
      <Stack gap="md">
        {preview.matches.map((match) => (
          <Paper key={match.personId} withBorder p="sm" radius="md">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Stack gap={0}>
                <Text size="sm" fw={600}>
                  Attach to {match.name} instead?
                </Text>
                <Text size="xs" c="dimmed">
                  They have the same{" "}
                  {match.matchedOn.length === 2
                    ? "email and number"
                    : match.matchedOn[0] === "email"
                      ? "email"
                      : "number"}
                  .
                </Text>
              </Stack>
              <Button
                variant="default"
                loading={add.isPending}
                ref={(element: HTMLButtonElement | null) => {
                  if (element === null) attachButtons.current.delete(match.personId);
                  else attachButtons.current.set(match.personId, element);
                }}
                onClick={() => {
                  if (asksForMainNumber(preview, match)) setAsking(match);
                  else attach(match);
                }}
              >
                Attach to {match.name}
              </Button>
            </Group>
          </Paper>
        ))}
        {preview.matches.length > 0 ? (
          <Text size="sm" c="dimmed">
            Or add them as a new person:
          </Text>
        ) : null}
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <TextInput label="First name" required {...form.getInputProps("firstName")} />
          <TextInput label="Last name" required {...form.getInputProps("lastName")} />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Email
            </Text>
            <Text size="sm">{preview.email ?? "None given"}</Text>
          </Stack>
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Number
            </Text>
            <Text size="sm">{preview.phone ?? "None given"}</Text>
          </Stack>
        </SimpleGrid>
        <Text size="sm">
          {preview.consent.length === 0
            ? "No consent was given on this form, so none is recorded."
            : `Consent to record: Yes to ${words(preview.consent)}, from the form ${preview.formName}.`}
          {preview.consent.includes("sms") && preview.phone === null
            ? " The form gave no number, so its yes to text messages can't be recorded."
            : ""}
        </Text>
        <Alert color={semanticColor("info")} variant="light">
          A form asks no birth date, so this person is recorded as an adult. If they are a child,
          add their birth date on their record: children are never messaged.
        </Alert>
        {add.isError ? (
          <Alert color={semanticColor("danger")} variant="light">
            {describeRpcError(add.error).message}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" loading={add.isPending} disabled={first === "" || last === ""}>
            Add as a new person
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * A yes to texts follows the number it was given for: attaching to someone texted at another number asks first
 * whether the form's number becomes their main one. Nobody is asked when the form gave no yes to texts or no number,
 * or the person has no number yet (the form's becomes theirs).
 */
function asksForMainNumber(preview: SubmissionPreview, match: Match): boolean {
  return (
    preview.consent.includes("sms") &&
    preview.phone !== null &&
    match.textsTo !== null &&
    !match.sameTextsNumber
  );
}

/** "Make (949) 555-0002 Carla's main number for texts?", with what each answer does. */
function AskMainNumber({
  name,
  formPhone,
  textsTo,
  pending,
  error,
  onYes,
  onNo,
  onBack,
}: {
  name: string;
  formPhone: string;
  textsTo: string;
  pending: boolean;
  error: string | null;
  onYes: () => void;
  onNo: () => void;
  onBack: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  // The Attach button that opened this is gone, so focus starts at the question.
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <Stack gap="sm">
      <Title order={3} size="h5" ref={heading} tabIndex={-1}>
        Make {formPhone} {name}&apos;s main number for texts?
      </Title>
      <Text size="sm">
        {name}&apos;s texts go to {textsTo}. This form agreed to texts at {formPhone}, so its yes to
        text messages can be recorded only for that number.
      </Text>
      <Text size="sm" c="dimmed">
        Yes: {formPhone} becomes their main number, {textsTo} is kept as a second one, and the yes
        is recorded. No: their numbers stay as they are and no text consent is recorded.
      </Text>
      {error !== null ? (
        <Alert color={semanticColor("danger")} variant="light">
          {error}
        </Alert>
      ) : null}
      <Group justify="flex-end" wrap="wrap">
        <Button variant="subtle" onClick={onBack}>
          Back
        </Button>
        <Button variant="default" loading={pending} onClick={onNo}>
          No, keep {textsTo} for texts
        </Button>
        <Button loading={pending} onClick={onYes}>
          Yes, make it the main number
        </Button>
      </Group>
    </Stack>
  );
}

/** What was done, in words: who, which consent was recorded and from where, and which newer answer was kept. */
function Result({
  result,
  onOpen,
  onDone,
}: {
  result: Added;
  onOpen: (personId: string) => void;
  onDone: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  // The button that was pressed is gone, so focus starts at what was done (UI standards, #310 review).
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <Stack gap="sm" role="status">
      <Title order={3} size="h5" ref={heading} tabIndex={-1}>
        {result.created
          ? `${result.name} is added to your people.`
          : `The message is attached to ${result.name}.`}
      </Title>
      <Text size="sm">
        {result.recorded.length === 0
          ? "No consent was recorded."
          : `Recorded: yes to ${words(result.recorded)}, from “${result.source}”.`}
      </Text>
      {result.kept.map((k) => (
        <Text key={k.channel} size="sm">
          Kept: {k.optedIn ? "yes" : "no"} to {CHANNEL_WORDS[k.channel]}, which they said on{" "}
          {new Date(k.recordedAt).toLocaleDateString()}, after this form.
        </Text>
      ))}
      {result.madePrimary && result.formPhone !== null ? (
        <Text size="sm">
          {result.formPhone} is now {result.name}&apos;s main number for texts; their other number
          is kept as a second one.
        </Text>
      ) : null}
      {result.withheld.map((w) => (
        <Text key={w.channel} size="sm">
          Not recorded: yes to {CHANNEL_WORDS[w.channel]}.{" "}
          {w.reason === "no_number" || w.textsTo === null
            ? "The form gave no number to text."
            : `${result.name}'s texts go to ${w.textsTo}, which this form didn't agree to.`}
        </Text>
      ))}
      {result.contactsAdded > 0 ? (
        <Text size="sm" c="dimmed">
          {result.contactsAdded === 1
            ? "1 contact method was added from the form."
            : `${String(result.contactsAdded)} contact methods were added from the form.`}
        </Text>
      ) : null}
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>
          Done
        </Button>
        <Button
          onClick={() => {
            onOpen(result.personId);
          }}
        >
          Open their record
        </Button>
      </Group>
    </Stack>
  );
}
